// Library Methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { ReportContext } from "src/contexts/reports/ReportContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import {
  getReportEyeText,
  getVisualField,
} from "src/utils/exams/examReportsHelper";
import { getLocalDateTimeWithZone } from "src/utils/date/dateHelper";
import {
  formatAsPercentage,
  toHumanReadable,
} from "src/utils/exams/legacyExamHelper";

// Constants
import { stimulusColorEnums } from "src/constants/enums";

const useLegacyVFColumns = () => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { externalExam } = useContext(ReportContext);
  const { eyeType } = useContext(ReportSectionContext);

  const localDateAndTimeWithZone = useMemo(() => {
    const examDate = externalExam?.date ?? externalExam?.data?.completionDate;
    return examDate
      ? getLocalDateTimeWithZone(examDate, clinicInfo?.timezone)
      : { date: "N/A", time: "N/A" };
  }, [
    externalExam?.date,
    externalExam?.data?.completionDate,
    clinicInfo?.timezone,
  ]);

  const thresholdingColumns = useMemo(() => {
    const firstColumn = [
      {
        title: t("Eye"),
        value: getReportEyeText(eyeType, t),
        isBlueText: true,
      },
      {
        title: t("Fixation Method"),
        value: t(toHumanReadable(externalExam?.data?.fixationMethod)),
        isBlueText: true,
      },
      {
        title: t("Fixation Loss"),
        value: formatAsPercentage(externalExam?.data?.fixationLoss),
        isBlueText: true,
      },
      {
        title: t("False Positives"),
        value: formatAsPercentage(externalExam?.data?.falsePositive),
        isBlueText: true,
      },
      {
        title: t("False Negatives"),
        value: formatAsPercentage(externalExam?.data?.falseNegative),
        isBlueText: true,
      },
    ];

    const secondColumn = [
      {
        title: t("Grid Type"),
        value: externalExam?.data?.gridType
          ? getVisualField(externalExam?.data?.gridType)
          : "",
        isBlueText: true,
      },
      {
        title: t("Speed Type"),
        value: t("Normal"),
        isBlueText: true,
      },
      {
        title: t("Stimulus"),
        value: externalExam?.data?.stimulusSize
          ? `${externalExam?.data?.stimulusSize}, ${t(stimulusColorEnums.WHITE)}`
          : "",
        isBlueText: true,
      },
    ];

    const thirdColumn = [
      { title: t("Date"), value: localDateAndTimeWithZone?.date },
      { title: t("Time_hour"), value: localDateAndTimeWithZone?.time },
    ];

    return { firstColumn, secondColumn, thirdColumn };
  }, [
    externalExam?.data?.gridType,
    externalExam?.data?.falseNegative,
    externalExam?.data?.falsePositive,
    externalExam?.data?.fixationLoss,
    externalExam?.data?.fixationMethod,
    externalExam?.data?.stimulusSize,
    localDateAndTimeWithZone,
    eyeType,
    t,
  ]);

  return { thresholdingColumns };
};

export default useLegacyVFColumns;
